import emailjs from '@emailjs/browser';
import React, { useEffect, useMemo, useState } from 'react';
import './ReportBugModal.css';
import { loadFromLocalStorage, loadRawLocalStorage, STORAGE_KEYS } from './utils';

const MAX_DESCRIPTION_CHARS = 2000;
const MAX_MESSAGE_CHARS = 9000;

const buildDiagnostics = () => {
    if (typeof window === 'undefined') return {};

    const history = loadFromLocalStorage(STORAGE_KEYS.game.history, []);
    const historyTail = Array.isArray(history) ? history.slice(-5) : history;
    const currentGame = loadFromLocalStorage(STORAGE_KEYS.game.current, null);

    return {
        timestamp: new Date().toISOString(),
        url: window.location?.href || null,
        userAgent: navigator?.userAgent || null,
        language: navigator?.language || null,
        platform: navigator?.platform || null,
        screen: {
            width: window.screen?.width ?? null,
            height: window.screen?.height ?? null,
            pixelRatio: window.devicePixelRatio ?? null
        },
        settings: {
            hardMode: loadFromLocalStorage(STORAGE_KEYS.settings.hardMode, null),
            darkMode: loadFromLocalStorage(STORAGE_KEYS.settings.darkMode, null),
            animationMode: loadFromLocalStorage(STORAGE_KEYS.settings.animationMode, null),
        },
        telemetryClientId: loadRawLocalStorage(STORAGE_KEYS.telemetry.clientId, null),
        currentGame,
        historyTail
    };
};

const ReportBugModal = ({ open, onClose }) => {
    const [description, setDescription] = useState('');
    const [email, setEmail] = useState('');
    const [isSending, setIsSending] = useState(false);

    useEffect(() => {
        if (open) {
            setDescription('');
            setEmail('');
            setIsSending(false);
        }
    }, [open]);

    const diagnostics = useMemo(() => (open ? buildDiagnostics() : null), [open]);

    if (!open) return null;

    const handleDescriptionChange = (e) => {
        const value = e.target.value;
        if (value.length > MAX_DESCRIPTION_CHARS) return;
        setDescription(value);
    };

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
    };

    const isValid = () => description.trim().length > 0;

    const handleSend = () => {
        if (!isValid() || isSending) return;
        setIsSending(true);

        const SERVICE_ID = 'service_0mduhlq';
        const TEMPLATE_ID = 'template_xn72sn8';
        const PUBLIC_KEY = 'jnkr7nsQYnO3vp7Xz';

        const diagnosticsText = diagnostics
            ? JSON.stringify(diagnostics, null, 2)
            : 'Diagnostics unavailable.';

        let message = `${description.trim()}\n\n--- Diagnostics ---\n${diagnosticsText}`;
        if (message.length > MAX_MESSAGE_CHARS) {
            message = `${message.slice(0, MAX_MESSAGE_CHARS)}\n\n--- Diagnostics truncated ---`;
        }

        const templateParams = {
            name: 'Bug Report',
            email: email.trim() || 'not provided',
            message,
            title: 'Bug Report'
        };

        emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY)
            .then((response) => {
                console.log('SUCCESS!', response.status, response.text);
                alert('Bug report sent. Thank you!');
                onClose();
            })
            .catch((err) => {
                console.log('FAILED...', err);
                alert(`Failed to send bug report: ${err.text || 'Unknown Error'}. Please try again later.`);
                setIsSending(false);
            });
    };

    return (
        <div className="report-bug-overlay" onClick={onClose}>
            <div className="report-bug-content" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={onClose} aria-label="Close">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="24"
                        viewBox="0 0 24 24"
                        width="24"
                        fill="currentColor"
                    >
                        <path d="M0 0h24v24H0V0z" fill="none" />
                        <path d="M18.3 5.71a1 1 0 00-1.41 0L12 10.59 7.11 5.7A1 1 0 105.7 7.11L10.59 12l-4.89 4.89a1 1 0 101.41 1.41L12 13.41l4.89 4.89a1 1 0 001.41-1.41L13.41 12l4.89-4.89a1 1 0 000-1.4z" />
                    </svg>
                </button>

                <h2>Report a Bug</h2>
                <p className="report-bug-text">
                    Tell us what went wrong. We'll include diagnostic game data to help troubleshoot.
                </p>

                <div className="form-group">
                    <textarea
                        name="description"
                        placeholder="Describe the bug (what you expected vs. what happened)"
                        value={description}
                        onChange={handleDescriptionChange}
                        rows={6}
                    />
                    <div className="char-count">{description.length}/{MAX_DESCRIPTION_CHARS}</div>
                </div>

                <div className="form-group">
                    <input
                        type="email"
                        name="email"
                        placeholder="Email (optional)"
                        value={email}
                        onChange={handleEmailChange}
                    />
                </div>

                <button
                    className="send-button"
                    disabled={!isValid() || isSending}
                    onClick={handleSend}
                >
                    Submit
                </button>
            </div>
        </div>
    );
};

export default ReportBugModal;
